import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LogOut, FileText, Upload, Clock, CheckCircle, 
  AlertCircle, Eye, Download, Trash2, Plus, ChevronRight,
  Building2, FolderOpen, AlertTriangle, Calendar, RefreshCw,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import axios from "axios";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "523343892543"; // México +52, número 3343892543

// Document Types - IDs must match backend REQUIRED_DOCUMENTS
const DOCUMENT_TYPES = [
  { id: "razon_social", nombre: "Nombre, denominación o razón social" },
  { id: "domicilio_fiscal", nombre: "Domicilio fiscal y para notificaciones" },
  { id: "opinion_sat", nombre: "Opinión de cumplimiento fiscal SAT" },
  { id: "manifestacion_licitantes", nombre: "Manifestación no sancionado" },
  { id: "propiedad_predio", nombre: "Propiedad o posesión del predio" },
  { id: "especificaciones_tecnicas", nombre: "Especificaciones técnicas del proyecto" },
  { id: "ubicacion_georeferenciada", nombre: "Ubicación georeferenciada" },
  { id: "monto_inversion", nombre: "Monto de inversión estimado" },
  { id: "manifestacion_marco_regulatorio", nombre: "Conocimiento marco regulatorio" },
  { id: "manifestacion_69b", nombre: "Manifestación Art. 69-B CFF" },
  { id: "acta_constitutiva", nombre: "Acta Constitutiva (Persona Moral)" },
  { id: "poder_representante", nombre: "Poder del representante legal" },
  { id: "identificacion_representante", nombre: "Identificación del representante" },
  { id: "cedula_rfc", nombre: "Cédula RFC" },
  { id: "estructura_accionaria", nombre: "Estructura accionaria y corporativa" },
];

// Helper to get auth headers
const getAuthConfig = () => {
  const token = localStorage.getItem('client_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const STATUS_CONFIG = {
  recabando_documentos: { label: "Recabando Documentos", color: "bg-amber-100 text-amber-800", icon: Clock },
  iniciando_tramite: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: FileText },
  solicitud_enviada: { label: "Solicitud Enviada a Autoridad", color: "bg-purple-100 text-purple-800", icon: Upload },
  permiso_aprobado: { label: "Permiso Aprobado", color: "bg-green-100 text-green-800", icon: CheckCircle },
  permiso_rechazado: { label: "Permiso Rechazado", color: "bg-red-100 text-red-800", icon: AlertCircle },
  // Legacy states for backwards compatibility
  documentos_pendientes: { label: "Recabando Documentos", color: "bg-amber-100 text-amber-800", icon: Clock },
  en_revision: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: Eye },
  expediente_completo: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: FileText },
  enviado_cne: { label: "Solicitud Enviada a Autoridad", color: "bg-purple-100 text-purple-800", icon: Upload },
  en_proceso_cne: { label: "Solicitud Enviada a Autoridad", color: "bg-purple-100 text-purple-800", icon: Clock },
  observaciones_cne: { label: "Observaciones", color: "bg-red-100 text-red-800", icon: AlertCircle },
  aprobado: { label: "Permiso Aprobado", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

// Documentos obligatorios por tipo de servicio (usando IDs)
const REQUIRED_DOCUMENTS = {
  "Permisos de Autoconsumo": [
    "razon_social", "domicilio_fiscal", "opinion_sat", "manifestacion_licitantes",
    "propiedad_predio", "especificaciones_tecnicas", "ubicacion_georeferenciada",
    "monto_inversion", "manifestacion_marco_regulatorio", "manifestacion_69b",
    "acta_constitutiva", "poder_representante", "identificacion_representante",
    "cedula_rfc", "estructura_accionaria"
  ],
  "Controles Volumétricos": [
    "acta_constitutiva", "cedula_rfc", "identificacion_representante",
    "opinion_sat", "especificaciones_tecnicas"
  ],
  "Gestión Integral": [
    "razon_social", "acta_constitutiva", "cedula_rfc", "identificacion_representante",
    "domicilio_fiscal", "opinion_sat", "especificaciones_tecnicas"
  ],
  "Suministro de Combustible": [
    "acta_constitutiva", "cedula_rfc", "identificacion_representante", "opinion_sat"
  ],
};

// Calcular días restantes según el estado del trámite
// Cada estado tiene su propio contador de días desde que cambió a ese estado
const calculateDaysRemaining = (tramite) => {
  const status = tramite.status;
  
  // Configuración de días por estado
  const STATUS_DAYS = {
    recabando_documentos: 30,
    documentos_pendientes: 30, // legacy
    iniciando_tramite: 15,
    en_revision: 15, // legacy
    expediente_completo: 15, // legacy
    solicitud_enviada: 90,
    enviado_cne: 90, // legacy
    en_proceso_cne: 90, // legacy
  };
  
  // Si está aprobado o rechazado, no mostrar días
  if (status === 'permiso_aprobado' || status === 'permiso_rechazado' || status === 'aprobado') {
    return { daysRemaining: 0, deadline: null, isExpired: false, deadlineType: 'none' };
  }
  
  // Buscar cuándo entró al estado actual
  const currentStatusEntry = tramite.status_history?.find(h => h.status === status);
  const statusDate = currentStatusEntry?.timestamp || currentStatusEntry?.date || tramite.updated_at || tramite.created_at;
  const startDate = new Date(statusDate);
  
  // Obtener días para este estado
  const daysForStatus = STATUS_DAYS[status] || 30;
  
  // Calcular deadline
  const deadline = new Date(startDate.getTime() + daysForStatus * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diffTime = deadline - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Determinar tipo de deadline para el mensaje
  let deadlineType = 'documentos';
  if (status === 'solicitud_enviada' || status === 'enviado_cne' || status === 'en_proceso_cne') {
    deadlineType = 'autoridad';
  } else if (status === 'iniciando_tramite' || status === 'expediente_completo') {
    deadlineType = 'tramite';
  }
  
  return { daysRemaining: diffDays, deadline, isExpired: diffDays < 0, deadlineType, totalDays: daysForStatus };
};

export default function ClientDashboard() {
  const [client, setClient] = useState(null);
  const [tramites, setTramites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTramite, setSelectedTramite] = useState(null);
  const [dataVersion, setDataVersion] = useState(0); // Force re-render on data updates
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState("");
  const [uploadTramiteId, setUploadTramiteId] = useState("");
  const navigate = useNavigate();

  // Restaurar trámite seleccionado desde localStorage
  useEffect(() => {
    const savedTramiteId = localStorage.getItem('client_selected_tramite');
    if (savedTramiteId && tramites.length > 0) {
      const tramite = tramites.find(t => t.id === savedTramiteId);
      if (tramite) {
        setSelectedTramite(tramite);
      }
    }
  }, [tramites]);

  // Guardar trámite seleccionado en localStorage
  useEffect(() => {
    if (selectedTramite) {
      localStorage.setItem('client_selected_tramite', selectedTramite.id);
    }
  }, [selectedTramite]);

  // Verificar documentos faltantes
  const getMissingDocuments = (tramite, tramiteDocs) => {
    const requiredDocIds = REQUIRED_DOCUMENTS[tramite.service_type] || [];
    const uploadedDocTypes = tramiteDocs.map(d => d.document_type);
    const missingIds = requiredDocIds.filter(docId => !uploadedDocTypes.includes(docId));
    // Devolver objetos con id y nombre
    return missingIds.map(id => {
      const docType = DOCUMENT_TYPES.find(dt => dt.id === id);
      return docType ? { id, nombre: docType.nombre } : { id, nombre: id };
    });
  };

  // Helper para obtener nombre de tipo de documento
  const getDocTypeName = (docId) => {
    const docType = DOCUMENT_TYPES.find(dt => dt.id === docId);
    return docType ? docType.nombre : docId;
  };

  const fetchData = useCallback(async () => {
    try {
      const config = getAuthConfig();
      const [clientRes, tramitesRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/me`, config),
        axios.get(`${API_URL}/api/client/tramites`, config),
        axios.get(`${API_URL}/api/client/documents`, config),
      ]);
      setClient(clientRes.data);
      // Create new array references to force React re-render
      setTramites([...tramitesRes.data]);
      setDocuments([...docsRes.data]);
      setDataVersion(v => v + 1); // Increment to force re-render
      
      // Actualizar el trámite seleccionado con datos frescos
      setSelectedTramite(prev => {
        if (prev) {
          const updated = tramitesRes.data.find(t => t.id === prev.id);
          return updated ? {...updated} : prev;
        }
        return prev;
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('client_token');
        navigate("/portal");
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/client/logout`, {}, getAuthConfig());
    } catch {
      // ignore
    }
    localStorage.removeItem('client_token');
    navigate("/portal");
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadDocType || !uploadTramiteId) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("tramite_id", uploadTramiteId);
    formData.append("document_type", uploadDocType);

    try {
      const config = getAuthConfig();
      await axios.post(`${API_URL}/api/client/documents/upload`, formData, {
        ...config,
        headers: { ...config.headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento subido exitosamente");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDocType("");
      setUploadTramiteId("");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al subir documento";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const config = getAuthConfig();
      const res = await axios.get(`${API_URL}/api/client/documents/${docId}/download`, {
        ...config,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Error al descargar documento");
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("¿Estás seguro de eliminar este documento?")) return;
    try {
      await axios.delete(`${API_URL}/api/client/documents/${docId}`, getAuthConfig());
      toast.success("Documento eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar documento");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F3] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#103F38]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F3]">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <PetrocontrolLogo className="h-8" />
          </a>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#152238]">{client?.company_name}</p>
              <p className="text-xs text-[#66727B]">{client?.contact_name}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-[#e2e4e0] text-[#66727B] hover:bg-[#F5F6F3]"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#152238] mb-2">
            Bienvenido, {client?.contact_name}
          </h1>
          <p className="text-[#66727B]">
            Aquí puedes dar seguimiento a tus trámites y subir documentos
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#103F38]/10 rounded-full">
                  <FolderOpen className="h-6 w-6 text-[#103F38]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#152238]">{tramites.length}</p>
                  <p className="text-sm text-[#66727B]">Trámites Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#152238]">{documents.length}</p>
                  <p className="text-sm text-[#66727B]">Documentos Subidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#152238]">
                    {documents.filter(d => d.validation_status === "approved").length}
                  </p>
                  <p className="text-sm text-[#66727B]">Docs. Aprobados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tramites Section - Vertical Layout */}
        <div className="space-y-6">
          {/* Tramites List */}
          <Card className="border-none shadow-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Mis Trámites</CardTitle>
                <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="bg-[#103F38] hover:bg-[#0c332d]"
                      disabled={tramites.length === 0}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Subir Documento
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Subir Documento</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Trámite</Label>
                        <Select value={uploadTramiteId} onValueChange={setUploadTramiteId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona el trámite" />
                          </SelectTrigger>
                          <SelectContent>
                            {tramites.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Documento</Label>
                        <Select value={uploadDocType} onValueChange={setUploadDocType}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona el tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {DOCUMENT_TYPES.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Archivo</Label>
                        <Input
                          type="file"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                        />
                        <p className="text-xs text-[#66727B]">
                          Formatos: PDF, Word, Excel, imágenes. Máximo 10MB.
                        </p>
                      </div>
                      <Button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="w-full bg-[#103F38] hover:bg-[#0c332d]"
                      >
                        {uploading ? "Subiendo..." : "Subir Documento"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {tramites.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="h-12 w-12 text-[#e2e4e0] mx-auto mb-4" />
                    <p className="text-[#66727B]">No tienes trámites activos</p>
                    <p className="text-sm text-[#66727B] mt-1">
                      Contacta a tu ejecutivo para iniciar un nuevo trámite
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tramites.map((tramite) => {
                      const statusConfig = STATUS_CONFIG[tramite.status] || STATUS_CONFIG.recabando_documentos;
                      const StatusIcon = statusConfig?.icon || Clock;
                      const tramiteDocs = documents.filter(d => d.tramite_id === tramite.id);
                      const missingDocs = getMissingDocuments(tramite, tramiteDocs);
                      const { daysRemaining, isExpired, deadlineType } = calculateDaysRemaining(tramite);
                      const isUrgent = daysRemaining <= 7 && daysRemaining > 0;
                      const isFinalStatus = tramite.status === 'permiso_aprobado' || tramite.status === 'permiso_rechazado' || tramite.status === 'aprobado';
                      
                      return (
                        <div
                          key={tramite.id}
                          onClick={() => setSelectedTramite(tramite)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedTramite?.id === tramite.id
                              ? "border-[#103F38] bg-[#103F38]/5"
                              : isExpired ? "border-red-300 bg-red-50" 
                              : isUrgent ? "border-orange-300 bg-orange-50"
                              : "border-[#e2e4e0] hover:border-[#103F38]/50"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-medium text-[#152238] mb-1">
                                {tramite.title}
                              </h3>
                              <p className="text-sm text-[#66727B] mb-2">
                                {tramite.service_type}
                              </p>
                              <div className="flex flex-wrap gap-2 mb-2">
                                <Badge className={statusConfig?.color || "bg-gray-100 text-gray-800"}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {statusConfig?.label || "Pendiente"}
                                </Badge>
                                {/* Deadline badge */}
                                {!isFinalStatus && deadlineType !== 'none' && (
                                  <Badge className={
                                    isExpired ? "bg-red-100 text-red-800" :
                                    isUrgent ? "bg-orange-100 text-orange-800" :
                                    deadlineType === 'autoridad' ? "bg-purple-100 text-purple-800" :
                                    "bg-gray-100 text-gray-800"
                                  }>
                                    <Calendar className="h-3 w-3 mr-1" />
                                    {isExpired ? "Plazo vencido" : 
                                     deadlineType === 'autoridad' ? `${daysRemaining} días (respuesta autoridad)` :
                                     deadlineType === 'tramite' ? "Revisando expediente" :
                                     `${daysRemaining} días restantes`}
                                  </Badge>
                                )}
                              </div>
                              {/* Missing documents alert */}
                              {missingDocs.length > 0 && !isFinalStatus && (
                                <div className="flex items-center gap-1 text-xs text-orange-600">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span>{missingDocs.length} documento(s) pendiente(s)</span>
                                </div>
                              )}
                            </div>
                            <ChevronRight className="h-5 w-5 text-[#66727B]" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

          {/* Tramite Details */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle className="text-lg">Detalles del trámite</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTramite ? (
                  (() => {
                    const tramiteDocs = documents.filter(d => d.tramite_id === selectedTramite.id);
                    const missingDocs = getMissingDocuments(selectedTramite, tramiteDocs);
                    const { daysRemaining, deadline, isExpired, deadlineType } = calculateDaysRemaining(selectedTramite);
                    const isUrgent = daysRemaining <= 7 && daysRemaining > 0;
                    const isFinalStatus = selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'permiso_rechazado' || selectedTramite.status === 'aprobado';
                    
                    return (
                  <div className="space-y-6">
                    {/* Tramite Header */}
                    <div>
                      <h3 className="font-medium text-[#152238] mb-1">
                        {selectedTramite.title}
                      </h3>
                      <p className="text-sm text-[#66727B]">
                        {selectedTramite.service_type}
                      </p>
                    </div>

                    {/* Deadline Alert */}
                    {!isFinalStatus && deadlineType !== 'none' && (
                      <Alert className={
                        isExpired ? "border-red-300 bg-red-50" :
                        isUrgent ? "border-orange-300 bg-orange-50" :
                        deadlineType === 'autoridad' ? "border-purple-200 bg-purple-50" :
                        "border-blue-200 bg-blue-50"
                      }>
                        <Calendar className={`h-4 w-4 ${
                          isExpired ? "text-red-600" : 
                          isUrgent ? "text-orange-600" : 
                          deadlineType === 'autoridad' ? "text-purple-600" :
                          "text-blue-600"
                        }`} />
                        <AlertDescription className={
                          isExpired ? "text-red-800" : 
                          isUrgent ? "text-orange-800" : 
                          deadlineType === 'autoridad' ? "text-purple-800" :
                          "text-blue-800"
                        }>
                          {isExpired 
                            ? "El plazo ha vencido. Contacta a tu ejecutivo."
                            : deadlineType === 'autoridad'
                            ? `La autoridad tiene ${daysRemaining} día(s) para responder (hasta ${deadline?.toLocaleDateString("es-MX")})`
                            : deadlineType === 'tramite'
                            ? "Estamos revisando tu expediente con la autoridad"
                            : `Tienes ${daysRemaining} día(s) para completar la documentación (hasta ${deadline?.toLocaleDateString("es-MX")})`
                          }
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Acuse de Solicitud Enviada a CNE */}
                    {selectedTramite.status === 'solicitud_enviada' && selectedTramite.acuse_document && (
                      <Alert className="border-purple-300 bg-purple-50">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <AlertDescription className="text-purple-800">
                          <div className="flex items-center justify-between">
                            <span>
                              Tu expediente fue enviado a la autoridad. Descarga el acuse de recibo.
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-400 text-purple-700 hover:bg-purple-100"
                              onClick={async () => {
                                try {
                                  const config = getAuthConfig();
                                  const res = await axios.get(`${API_URL}/api/client/tramites/${selectedTramite.id}/acuse/download`, {
                                    ...config,
                                    responseType: "blob",
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.setAttribute("download", selectedTramite.acuse_document?.original_filename || "acuse_solicitud.pdf");
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  toast.success("Acuse descargado correctamente");
                                } catch {
                                  toast.error("Error al descargar acuse");
                                }
                              }}
                              data-testid="download-acuse-cne"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Descargar Acuse CNE
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Approved/Rejected with Download */}
                    {isFinalStatus && selectedTramite.resolution_document && (
                      <Alert className={selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'aprobado' 
                        ? "border-green-300 bg-green-50" 
                        : "border-red-300 bg-red-50"
                      }>
                        {selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'aprobado' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <AlertDescription className={selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'aprobado' 
                          ? "text-green-800" 
                          : "text-red-800"
                        }>
                          <div className="flex items-center justify-between">
                            <span>
                              {selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'aprobado'
                                ? "¡Tu permiso ha sido aprobado!"
                                : "Tu solicitud fue rechazada. Contacta a tu ejecutivo para más información."
                              }
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className={selectedTramite.status === 'permiso_aprobado' || selectedTramite.status === 'aprobado'
                                ? "border-green-400 text-green-700 hover:bg-green-100"
                                : "border-red-400 text-red-700 hover:bg-red-100"
                              }
                              onClick={async () => {
                                try {
                                  const config = getAuthConfig();
                                  const res = await axios.get(`${API_URL}/api/client/tramites/${selectedTramite.id}/resolution/download`, {
                                    ...config,
                                    responseType: "blob",
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.setAttribute("download", selectedTramite.resolution_document?.filename || "resolucion.pdf");
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                } catch {
                                  toast.error("Error al descargar documento");
                                }
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Descargar Resolución
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* TWO COLUMNS: Documents Checklist | History */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* LEFT COLUMN: Document Checklist */}
                      <div className="space-y-4">
                        <p className="text-xs font-medium text-[#66727B] uppercase mb-2">
                          CHECKLIST DE DOCUMENTOS
                        </p>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                          {DOCUMENT_TYPES.map((req) => {
                            const doc = tramiteDocs.find(d => d.document_type === req.id);
                            const hasDoc = !!doc;
                            const validationStatus = doc?.validation_status || 'none';
                            
                            // Determine row styling based on status
                            let rowBg = 'bg-gray-50';
                            let borderColor = 'border-gray-200';
                            let checkboxColor = 'bg-gray-200 border-gray-300';
                            let checkIcon = null;
                            
                            if (hasDoc) {
                              if (validationStatus === 'approved') {
                                rowBg = 'bg-green-50';
                                borderColor = 'border-green-300';
                                checkboxColor = 'bg-green-500 border-green-500';
                                checkIcon = <CheckCircle className="h-4 w-4 text-white" />;
                              } else if (validationStatus === 'rejected') {
                                rowBg = 'bg-red-100';
                                borderColor = 'border-red-400 border-2';
                                checkboxColor = 'bg-red-500 border-red-500';
                                checkIcon = <AlertCircle className="h-4 w-4 text-white" />;
                              } else {
                                rowBg = 'bg-yellow-50';
                                borderColor = 'border-yellow-300';
                                checkboxColor = 'bg-yellow-100 border-yellow-400';
                                checkIcon = <Clock className="h-4 w-4 text-yellow-600" />;
                              }
                            }
                            
                            return (
                              <div key={`${req.id}-${validationStatus}-${dataVersion}`} className={`p-3 rounded-lg border ${rowBg} ${borderColor}`}>
                                <div className="flex items-start gap-3">
                                  {/* Checkbox indicator */}
                                  <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 border ${checkboxColor}`}>
                                    {checkIcon}
                                  </div>
                                  
                                  {/* Document info */}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${hasDoc && validationStatus === 'approved' ? 'text-green-700' : hasDoc && validationStatus === 'rejected' ? 'text-red-800 font-semibold' : 'text-[#152238]'}`}>
                                      {req.nombre}
                                      {hasDoc && validationStatus === 'rejected' && (
                                        <span className="ml-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded">RECHAZADO</span>
                                      )}
                                    </p>
                                    {hasDoc && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <FileText className={`h-3 w-3 ${validationStatus === 'rejected' ? 'text-red-400' : 'text-gray-400'}`} />
                                        <p className={`text-xs truncate ${validationStatus === 'rejected' ? 'text-red-600' : 'text-gray-500'}`}>
                                          {doc.original_filename} ({new Date(doc.uploaded_at).toLocaleDateString('es-MX')})
                                        </p>
                                      </div>
                                    )}
                                    {/* Rejection reason - PROMINENTLY DISPLAYED */}
                                    {hasDoc && validationStatus === 'rejected' && doc.validation_notes && (
                                      <div className="mt-3 p-3 bg-red-200 border border-red-400 rounded-lg">
                                        <div className="flex items-start gap-2">
                                          <AlertCircle className="h-5 w-5 text-red-700 flex-shrink-0 mt-0.5" />
                                          <div>
                                            <p className="text-sm font-bold text-red-800 mb-1">Observación del Administrador:</p>
                                            <p className="text-sm text-red-900 leading-relaxed">
                                              {doc.validation_notes}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Action buttons */}
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {hasDoc ? (
                                      <>
                                        {/* Download */}
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 w-8 p-0 border-blue-300 text-blue-600 hover:bg-blue-50"
                                          onClick={() => handleDownload(doc.id, doc.original_filename)}
                                          title="Descargar"
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                        
                                        {/* Replace - only if not approved or if rejected */}
                                        {(validationStatus !== 'approved' || validationStatus === 'rejected') && !isFinalStatus && (
                                          <label className="cursor-pointer">
                                            <input
                                              type="file"
                                              className="hidden"
                                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                              onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                try {
                                                  const formData = new FormData();
                                                  formData.append("file", file);
                                                  const config = getAuthConfig();
                                                  await axios.put(`${API_URL}/api/client/documents/${doc.id}/replace`, formData, {
                                                    ...config,
                                                    headers: { 
                                                      ...config.headers,
                                                      "Content-Type": "multipart/form-data" 
                                                    }
                                                  });
                                                  toast.success("Documento reemplazado correctamente");
                                                  fetchData();
                                                } catch (err) {
                                                  toast.error(err.response?.data?.detail || "Error al reemplazar documento");
                                                }
                                                e.target.value = '';
                                              }}
                                            />
                                            <div className="h-8 w-8 flex items-center justify-center border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-md cursor-pointer" title="Reemplazar">
                                              <RefreshCw className="h-4 w-4" />
                                            </div>
                                          </label>
                                        )}
                                      </>
                                    ) : (
                                      /* Upload button when no document */
                                      !isFinalStatus && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-400">Sin documento</span>
                                          <label className="cursor-pointer">
                                            <input
                                              type="file"
                                              className="hidden"
                                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                              onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                try {
                                                  const formData = new FormData();
                                                  formData.append("file", file);
                                                  formData.append("document_type", req.id);
                                                  formData.append("tramite_id", selectedTramite.id);
                                                  const config = getAuthConfig();
                                                  await axios.post(`${API_URL}/api/client/documents/upload`, formData, {
                                                    ...config,
                                                    headers: { 
                                                      ...config.headers,
                                                      "Content-Type": "multipart/form-data" 
                                                    }
                                                  });
                                                  toast.success("Documento subido correctamente");
                                                  fetchData();
                                                } catch (err) {
                                                  toast.error(err.response?.data?.detail || "Error al subir documento");
                                                }
                                                e.target.value = '';
                                              }}
                                            />
                                            <div className="flex items-center gap-1 px-3 py-1.5 border border-[#103F38] text-[#103F38] hover:bg-[#103F38]/10 rounded-md cursor-pointer text-sm font-medium">
                                              <Upload className="h-4 w-4" />
                                              Subir
                                            </div>
                                          </label>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="pt-2 border-t">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#66727B]">Progreso</span>
                            <span className="font-medium text-[#103F38]">
                              {tramiteDocs.filter(d => d.validation_status === 'approved').length}/{DOCUMENT_TYPES.length}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-[#103F38] h-2 rounded-full transition-all"
                              style={{ width: `${(tramiteDocs.filter(d => d.validation_status === 'approved').length / DOCUMENT_TYPES.length) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* RIGHT COLUMN: Status History */}
                      <div>
                        <p className="text-xs font-medium text-[#66727B] uppercase mb-2">
                          Historial de Estatus
                        </p>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {selectedTramite.status_history?.slice().reverse().map((entry, idx) => {
                            const config = STATUS_CONFIG[entry.status] || STATUS_CONFIG.recabando_documentos;
                            const bgColor = config?.color?.split(" ")[0] || "bg-gray-100";
                            return (
                              <div key={idx} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-3 h-3 rounded-full ${bgColor}`}></div>
                                  {idx < (selectedTramite.status_history?.length - 1) && (
                                    <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
                                  )}
                                </div>
                                <div className="pb-4">
                                  <p className="text-sm font-medium text-[#152238]">
                                    {config?.label || "Estado"}
                                  </p>
                                  {entry.notes && (
                                    <p className="text-xs text-[#66727B] mt-0.5">{entry.notes}</p>
                                  )}
                                  <p className="text-xs text-[#66727B] mt-1">
                                    {formatDate(entry.timestamp || entry.date)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                          {(!selectedTramite.status_history || selectedTramite.status_history.length === 0) && (
                            <p className="text-sm text-[#66727B] text-center py-4">
                              Sin historial de cambios
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedTramite.description && (
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-xs font-medium text-[#66727B] uppercase mb-1">
                          Descripción
                        </p>
                        <p className="text-sm text-[#152238]">
                          {selectedTramite.description}
                        </p>
                      </div>
                    )}
                  </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-[#e2e4e0] mx-auto mb-4" />
                    <p className="text-[#66727B]">Selecciona un trámite para ver los detalles</p>
                  </div>
                )}
              </CardContent>
            </Card>
        </div>
      </main>
      
      {/* WhatsApp Floating Button */}
      <a
        href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=Hola%2C%20tengo%20una%20consulta%20sobre%20mi%20tr%C3%A1mite`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110 z-50 flex items-center gap-2"
        data-testid="whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="hidden sm:inline font-medium">¿Dudas?</span>
      </a>
    </div>
  );
}
